import React, { useEffect } from "react";

import { useForm } from "react-hook-form";

import {
  ButtonIcon,
  IconChevron,
  IconMagnifyingGlass,
  Label
} from "@busbud/horizon";

import { AffiliateCheckbox } from "@app/components/landing-pages/affiliate-checkbox";
import type { SearchFormStyleVariant } from "@app/components/search-form/search-form";
import { setIsSearchFormExpanded } from "@app/components/search-form/search-form-client-state";
import { SearchFormContainer } from "@app/components/search-form/search-form-container";
import { useIntlProvider } from "@app/components/search-form-hydrated/autocomplete-input-hydrated/hooks/use-intl-provider";
import {
  getLegacySearchStateValues,
  useAffiliateOptin,
  useDefaultSearchLocationValues,
  useLegacySearchFormPassengers,
  useSearchLocationValues
} from "@app/components/search-form-hydrated/autocomplete-input-hydrated/hooks/use-search-state-hooks";
import {
  ancillaryRedirectAffiliateAndShowBusbudResults,
  redirectToResultsPage
} from "@app/components/search-form-hydrated/autocomplete-input-hydrated/search-redirect-helpers";
import type { SearchFormValues } from "@app/components/search-form-hydrated/search-form-hydrated";
import { useWhitelabelContext } from "@app/context/whitelabel-context";
import { getPopUnderAffiliate } from "@app/helpers/affiliate";
import { useAppDispatch, useLiteAppContext } from "@app/helpers/hooks";
import { LocalStorage } from "@app/lib/bowser-storage/local-storage";
import { getPassengerCountTranslation } from "@app/modules/search/helpers/passenger-input";
import { saveRecentSearch } from "@app/modules/search/helpers/recent-searches";
import { getSearchQuery } from "@app/modules/search/helpers/submit";
import { useDateInputs } from "@app/modules/search/store/hooks/date-inputs";
import { usePassengers } from "@app/modules/search/store/hooks/passengers";
import {
  setAffiliateOptin,
  setOutboundDate
} from "@app/modules/search/store/slices/search-form";
import {
  clickedAffiliateCheckbox,
  searchedRoute,
  toggledSearchVisibility
} from "@app/tracking/search-tracking";
import type { SearchFormMergedState } from "@app/types/search-types";
import { getTomorrowDateFormatted } from "@app/utils/dates-without-moment";
import { yieldToMain } from "@app/utils/scheduler-yield";
import { appendQueryToCurrentLocation } from "@app/utils/window";

import type { SubmitHandler } from "react-hook-form";

type SearchFormValuesLite = Pick<SearchFormValues, "origin" | "destination">;

export interface Props {
  style_variant?: SearchFormStyleVariant;
  with_affiliate_checkbox?: boolean;
  profile_ids?: number[];
}

const SearchFormCompactButton: React.FC = props => {
  return (
    <ButtonIcon
      id="search-form-compact-submit-button"
      data-cy="search-submit"
      className="shrink-0"
      isSubmit
      role="button"
      size="lg"
      appearance="primary"
      variant="contained"
      {...props}
    >
      <IconMagnifyingGlass size="md" />
    </ButtonIcon>
  );
};

const SearchFormCompactTripInfo: React.FC<{
  origin: React.ReactNode;
  destination: React.ReactNode;
  footer: React.ReactNode;
}> = ({ origin, destination, footer }) => {
  return (
    <div className="flex flex-col">
      <div className="flex shrink-0 items-start">
        {origin}
        <IconChevron
          variant="right"
          size="md"
          className="shrink-0 self-center text-icon-color-primary"
        />
        {destination}
      </div>
      {footer}
    </div>
  );
};

const SearchFormCompactWrapper: React.FC<
  React.PropsWithChildren<{
    button: React.ReactNode;
    onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  }>
> = ({ button, children, ...props }) => {
  return (
    <div className="lg:border-r flex w-full cursor-pointer justify-between rounded-md bg-color-canvas-primary p-100 pl-200 shadow-md">
      <div
        data-testid="search-form-compact-wrapper"
        className="w-full overflow-x-auto"
        {...props}
      >
        {children}
      </div>
      {button}
    </div>
  );
};

export const SearchFormCompact: React.FC<Props> = ({
  style_variant,
  profile_ids,
  with_affiliate_checkbox = false,
  ...props
}) => {
  const { liteTranslator, features, tracker } = useLiteAppContext();
  const dispatch = useAppDispatch();
  const { getLocalizedDate } = useIntlProvider();
  const { whitelabel: dynamic_whitelabel } = useWhitelabelContext();

  const { origin, destination } = useDefaultSearchLocationValues();
  const { outbound_date, return_date } = useDateInputs();
  const { total_passengers_count } = usePassengers();
  const legacy_passenger_state = useLegacySearchFormPassengers();
  const { recent_searches } = useSearchLocationValues();
  const affiliate_optin_redux = useAffiliateOptin();

  const passengers_count_translation = getPassengerCountTranslation(
    liteTranslator,
    total_passengers_count
  );

  const localized_outbound_date = outbound_date
    ? getLocalizedDate(outbound_date)
    : "";

  useEffect(
    function setDefaultOutboundDate() {
      if (!outbound_date) {
        // If the outbound date is not set, we default it to tomorrow's date
        // This is to ensure that the form has a valid date to submit
        dispatch(setOutboundDate(getTomorrowDateFormatted()));
      }
    },
    [outbound_date, dispatch]
  );

  const onSubmit: SubmitHandler<SearchFormValuesLite> = async () => {
    const { whitelabel_context, ...legacy_state_values } =
      getLegacySearchStateValues();
    const { lang, whitelabel, push_state_disabled } = legacy_state_values;

    const state: Omit<SearchFormMergedState, "passengers"> = {
      origin: origin.value,
      destination: destination.value,
      affiliate_optin: affiliate_optin_redux,
      outbound_date: outbound_date || "",
      return_date: return_date || "",
      vehicle_category: undefined,
      profile_ids,
      ...legacy_passenger_state,
      ...legacy_state_values
    };

    if (!(origin.value && destination.value && outbound_date)) {
      return;
    }

    const search_with_accomodation_affiliate = affiliate_optin_redux
      ? getPopUnderAffiliate()
      : "none";

    await yieldToMain();

    const results_options = {
      lang,
      features,
      whitelabel:
        whitelabel ||
        whitelabel_context?.whitelabel_name ||
        dynamic_whitelabel?.name,
      state,
      tracker
    };

    const browser_storage = LocalStorage.fromWindow(window);

    saveRecentSearch(browser_storage, "origin", recent_searches.origin);
    saveRecentSearch(
      browser_storage,
      "destination",
      recent_searches.destination
    );

    // Tracking
    tracker?.track(
      searchedRoute({
        state: {
          ...state,
          origin: origin.value,
          destination: destination.value,
          outbound_date
        },
        initial_origin: origin.value,
        initial_destination: destination.value,
        search_with_accomodation_affiliate
      })
    );

    // Redirect
    if (!push_state_disabled) {
      // This allows us to fill the form when the user clicks back from results page
      appendQueryToCurrentLocation(getSearchQuery(results_options, state));
    }

    if (search_with_accomodation_affiliate !== "none") {
      // Safari popup block: Safari browser will block popups and partners page opening if window.open() is not immediate. async functions are not allowed here
      ancillaryRedirectAffiliateAndShowBusbudResults(
        search_with_accomodation_affiliate,
        results_options
      );
      return;
    }

    redirectToResultsPage(results_options);
  };

  const { handleSubmit } = useForm<SearchFormValuesLite>({
    defaultValues: {
      origin: origin.formatted,
      destination: destination.formatted
    }
  });

  return (
    <>
      <SearchFormContainer
        data-testid="search-form-compact"
        style_variant={style_variant}
        className="my-150"
        onSubmit={handleSubmit(onSubmit)}
        {...props}
      >
        <SearchFormCompactWrapper
          onClick={() => {
            setIsSearchFormExpanded(true);
            tracker?.asyncTrack(
              toggledSearchVisibility(
                "search-form-compact",
                "true",
                "search-form-compact-wrapper"
              )
            );
          }}
          button={<SearchFormCompactButton />}
        >
          <SearchFormCompactTripInfo
            origin={
              <Label
                as="span"
                size="lg"
                fontWeight="bold"
                className="line-clamp-2 min-w-0 flex-1 shrink"
              >
                {origin.formatted}
              </Label>
            }
            destination={
              <Label
                as="span"
                size="lg"
                fontWeight="bold"
                className="line-clamp-2 min-w-0 flex-1 shrink"
              >
                {destination.formatted}
              </Label>
            }
            footer={
              <Label
                as="span"
                size="md"
                fontWeight="bold"
                className="truncate text-color-tertiary"
              >
                {localized_outbound_date}, {passengers_count_translation}
              </Label>
            }
          />
        </SearchFormCompactWrapper>
      </SearchFormContainer>
      {!!with_affiliate_checkbox && (
        <AffiliateCheckbox
          name="affiliate_optin"
          className="mt-150"
          isChecked={affiliate_optin_redux}
          onChange={e => {
            const updated_value: boolean = !!(e?.currentTarget).checked;
            dispatch(setAffiliateOptin(updated_value));
            tracker?.asyncTrack(
              clickedAffiliateCheckbox(getPopUnderAffiliate(), updated_value)
            );
          }}
        />
      )}
    </>
  );
};
